import { createTheme } from '@mui/material/styles';

export const coolWhiteTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2' },
    background: { default: '#f9fafe', paper: '#fff' },
    text: { primary: '#222', secondary: '#555' },
  },
  typography: {
    fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

/**
 * THE PROFESSIONAL DARK WORKSPACE.
 *
 * <h2>Three depths, not one</h2>
 *
 * A single flat charcoal makes a dialog indistinguishable from the canvas behind it and a menu
 * indistinguishable from the dialog it opened out of. So the surfaces are a ladder, lightening as
 * they come forward — which is how elevation reads without a border on every edge:
 *
 * <pre>
 *   #0d1117  default   the workspace behind everything, and the chart canvas
 *   #161b22  paper     dialogs, the toolbar strip, the sidebar
 *   #1f2630  elevated  menus and anything that opens out of a paper surface
 * </pre>
 *
 * <p>Slate rather than neutral grey: the accents this application has to live with — PTA amber,
 * PTB cyan, CTS violet — all sit on the cool side, and a warm-neutral background makes the amber
 * in particular look muddy.
 *
 * <h2>Text is #e6edf3, not #ffffff</h2>
 *
 * Pure white on near-black is the commonest mistake in a dark theme: the contrast is 19:1, well
 * past what legibility needs, and at small sizes it haloes. #e6edf3 on #0d1117 is **14.5:1** —
 * comfortably past the 7:1 that counts as AAA — and reads as paper rather than as a lightbulb.
 * The secondary is #9aa7b4 at **7.3:1**, still AAA, so a caption is quieter without being faint.
 *
 * <h2>The primary is LIGHTENED, and that is the one thing a light theme's palette cannot keep</h2>
 *
 * `#1976d2` is the light theme's blue and it is 3.1:1 against #0d1117 — under the 4.5:1 that
 * ordinary text needs, so a primary-coloured link or an outlined button's border would be the
 * least legible thing on the screen. `#58a6ff` is 7.4:1 at the same hue. This is the same
 * argument `indicatorPalette` already makes for the RSI line, where one stored hex could not
 * serve both themes either.
 */
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#58a6ff', dark: '#1f6feb', light: '#79c0ff', contrastText: '#0d1117' },
    secondary: { main: '#bc8cff' },
    error: { main: '#f85149' },
    warning: { main: '#d29922' },
    success: { main: '#3fb950' },
    info: { main: '#58a6ff' },
    background: { default: '#0d1117', paper: '#161b22' },
    text: { primary: '#e6edf3', secondary: '#9aa7b4', disabled: '#6b7681' },
    divider: 'rgba(240, 246, 252, 0.12)',
    action: {
      hover: 'rgba(177, 186, 196, 0.10)',
      selected: 'rgba(177, 186, 196, 0.16)',
      disabled: 'rgba(230, 237, 243, 0.30)',
      disabledBackground: 'rgba(230, 237, 243, 0.08)',
    },
  },
  typography: {
    fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    /*
     * The third depth. MUI's own dark elevation tints `paper` with a white overlay per elevation
     * step, which on this palette produces a blue-grey wash rather than a lift — so the raised
     * surfaces are set explicitly and the overlay is turned off.
     */
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: '#1f2630' },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: { backgroundColor: '#1f2630' },
      },
    },
    /*
     * The app bar keeps the PRIMARY colour in light mode and takes a surface in dark. A saturated
     * blue bar above a near-black workspace is the single loudest thing on the screen and it is
     * the one element that carries no information — so in dark it becomes the top of the ladder
     * rather than an accent.
     */
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: { backgroundColor: '#161b22', backgroundImage: 'none' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#1f2630', color: '#e6edf3', fontSize: 12,
          border: '1px solid rgba(240, 246, 252, 0.12)' },
        arrow: { color: '#1f2630' },
      },
    },
    /*
     * Toolbar icons. Many are drawn with `currentColor` and several are bitmaps that were authored
     * for a light bar; `text.primary` keeps the drawn ones legible and the bitmaps are handled
     * separately — see the toolbar's own note.
     */
    MuiIconButton: {
      styleOverrides: {
        root: { color: '#e6edf3' },
      },
    },
  },
});

export const skyBlueTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#38bdf8' },
    background: { default: '#e0f7fa', paper: '#b3e5fc' },
    text: { primary: '#155774', secondary: '#4f6e8c' },
  },
  typography: {
    fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});

export const feminineTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e573b5' },
    background: { default: '#fff0f6', paper: '#ffe4ec' },
    text: { primary: '#a21caf', secondary: '#d946ef' },
  },
  typography: {
    fontFamily: '"Nunito", "Roboto", "Helvetica", "Arial", sans-serif',
  },
});
